import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextInput,
  Button,
  Title,
  Text,
  Group,
  Stack,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { authClient } from '../../lib/auth-client';
import { useBetterAuth } from '../../context/BetterAuthProvider';
import { useNavigate } from 'react-router-dom';

interface LoginFormValues {
  email: string;
  otp?: string;
}

const LoginView = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [email, setEmail] = useState('');
  const { setIsAuthenticated, setUser } = useBetterAuth();
  const navigate = useNavigate();

  const form = useForm<LoginFormValues>({
    initialValues: {
      email: '',
      otp: '',
    },
    validate: {
      email: (value) => (!showOtpField && !value ? 'Email is required' : null),
      otp: (value) =>
        showOtpField && !value ? 'Verification code is required' : null,
    },
  });

  const handleEmailSubmit = async (values: LoginFormValues) => {
    if (!values.email) return;

    setIsLoading(true);
    try {
      const { error } = await authClient.emailOtp.sendVerificationOtp({
        email: values.email,
        type: 'sign-in',
      });

      if (error) {
        throw new Error(error.message || 'Failed to send OTP');
      }

      setEmail(values.email);
      setShowOtpField(true);

      notifications.show({
        title: 'Verification Code Sent',
        message: `A verification code has been sent to ${values.email}`,
        color: 'green',
      });
    } catch (error) {
      console.error('Error sending OTP:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send OTP',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (values: LoginFormValues) => {
    if (!values.otp) return;

    setIsLoading(true);
    try {
      const { error } = await authClient.signIn.emailOtp({
        email: email,
        otp: values.otp,
      });

      if (error) {
        throw new Error(error.message || 'Invalid OTP');
      }

      // Get the fresh session to update the provider state
      const session = await authClient.getSession();
      if (session.data?.session && session.data?.user) {
        setIsAuthenticated(true);
        setUser(session.data.user);

        notifications.show({
          title: 'Login Successful',
          message: 'You have been logged in successfully',
          color: 'green',
        });

        // Navigate to users page after successful login
        navigate('/users?sortBy=createdAt&order=desc');
      } else {
        throw new Error('Failed to get session after login');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      notifications.show({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to verify OTP',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (values: LoginFormValues) => {
    if (showOtpField) {
      handleOtpSubmit(values);
    } else {
      handleEmailSubmit(values);
    }
  };

  const handleBackToEmail = () => {
    setShowOtpField(false);
    setEmail('');
    form.reset();
  };

  return (
    <Container
      size="sm"
      h="100vh"
      style={{ display: 'flex', alignItems: 'center' }}>
      <Paper shadow="md" p="xl" radius="md" w="100%" maw={400} mx="auto">
        <Title order={2} ta="center" mb="md">
          Welcome Back
        </Title>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {showOtpField ? (
              <>
                <Alert color="blue" variant="light">
                  We've sent a verification code to{' '}
                  <Text component="span" fw={600}>
                    {email}
                  </Text>
                  <br />
                  Enter it below to verify your email.
                </Alert>

                <TextInput
                  label="Verification Code"
                  placeholder="Enter 6-digit code"
                  {...form.getInputProps('otp')}
                  disabled={isLoading}
                />

                <Group grow>
                  <Button type="submit" loading={isLoading}>
                    Verify Code
                  </Button>
                  <Button
                    variant="subtle"
                    onClick={handleBackToEmail}
                    disabled={isLoading}>
                    Back to Email
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <TextInput
                  label="Email Address"
                  placeholder="Enter your email"
                  type="email"
                  {...form.getInputProps('email')}
                  disabled={isLoading}
                />

                <Button type="submit" fullWidth loading={isLoading}>
                  Get Verification Code
                </Button>
              </>
            )}
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export default LoginView;
